import {useMutation, useQuery} from '@apollo/client';
import * as React from 'react';
import {Link, Route, Routes} from 'react-router';

import {WodDetailScreen} from './WodDetailScreen';

import {MUTATION_STUDENT_COMPLETION, MUTATION_STUDENT_FAVORITE, QUERY_STUDENT_WODS} from '../studentQueries';
import {StudentTheme} from '../studentTheme';
import {Wod, acaoDeFavorito, addDays, dataDeConclusao, dayLabel, parseProgramIds, toDateParam} from '../wodsDay';

interface Props {
  theme: StudentTheme;
  /** settings.screens.<tela>.programFilter da conta, quando houver. */
  programFilter?: string | null;
}

interface WodsData {
  wods?: Wod[] | null;
}

interface ListProps extends Props {
  dia: Date;
  setDia: React.Dispatch<React.SetStateAction<Date>>;
}

const WodsList: React.FC<ListProps> = ({theme, programFilter, dia, setDia}) => {

  const programIds = React.useMemo(() => parseProgramIds(programFilter), [programFilter]);

  const {data, loading, error, refetch} = useQuery<WodsData>(QUERY_STUDENT_WODS, {
    variables: {
      date: toDateParam(dia),
      // Lista vazia significaria "nenhum programa"; o certo e omitir o filtro.
      ...(programIds.length > 0 ? {programIds} : {}),
    },
  });

  const wods = data?.wods ?? [];

  // As mutations de favorito e conclusao devolvem so uma String, entao o cache
  // nao tem como se atualizar sozinho: a lista do dia e recarregada apos cada
  // acao, que e quem sabe recalcular completedOnDate e isFavorited.
  const [favoritar] = useMutation(MUTATION_STUDENT_FAVORITE);
  const [concluir] = useMutation(MUTATION_STUDENT_COMPLETION);

  const [ocupado, setOcupado] = React.useState<string | null>(null);
  const [erro, setErro] = React.useState<string | null>(null);

  // Concluir e irreversivel — a API nao tem mutation para desmarcar — e o
  // after_create publica um post no feed e consome um check-in de quem esta em
  // plano por check-in. Um toque so, sem volta, seria caro demais.
  const [confirmando, setConfirmando] = React.useState<string | null>(null);

  React.useEffect(() => setConfirmando(null), [dia]);

  const agir = async (chave: string, executar: () => Promise<unknown>) => {
    if (ocupado) return;

    setOcupado(chave);
    setErro(null);

    try {
      await executar();
      await refetch();
    } catch (falha) {
      // A recusa do backend ("treino ja concluido", falta de permissao) e
      // informacao para o aluno, nao ruido para engolir.
      setErro((falha as Error).message || 'Não foi possível concluir a ação.');
    } finally {
      setOcupado(null);
      setConfirmando(null);
    }
  };

  return (
    <div className="wods">
      <nav className="wods__dias" style={{borderColor: theme.border}}>
        <button
          type="button"
          className="wods__seta"
          onClick={() => setDia((atual) => addDays(atual, -1))}
          aria-label="Dia anterior"
        >
          <i className="fa-light fa-chevron-left" aria-hidden="true" />
        </button>

        <span className="wods__dia-atual">{dayLabel(dia)}</span>

        <button
          type="button"
          className="wods__seta"
          onClick={() => setDia((atual) => addDays(atual, 1))}
          aria-label="Próximo dia"
        >
          <i className="fa-light fa-chevron-right" aria-hidden="true" />
        </button>
      </nav>

      {erro && (
        <p className="student-screen__estado" style={{color: theme.neutral}} role="alert">
          {erro}
        </p>
      )}

      {loading && <p className="student-screen__estado">Carregando…</p>}

      {error && !loading && (
        <div className="student-screen__estado">
          <p>Não foi possível carregar os treinos.</p>
          <button type="button" className="student-app__retry" onClick={() => refetch()}>
            Tentar de novo
          </button>
        </div>
      )}

      {!loading && !error && wods.length === 0 && (
        <p className="student-screen__estado">Nenhum treino para este dia.</p>
      )}

      {!loading &&
        !error &&
        wods.map((wod) => (
          <article key={wod.id} className="wod-card" style={{backgroundColor: theme.body, borderColor: theme.border}}>
            {wod.cover?.uri && <img className="wod-card__capa" src={wod.cover.uri} alt="" loading="lazy" />}

            <div className="wod-card__conteudo">
              <header className="wod-card__topo">
                <h2 className="wod-card__nome">
                  <Link to={wod.id} className="wod-card__link" style={{color: theme.text}}>
                    {wod.name}
                  </Link>
                </h2>
                {wod.completedOnDate && (
                  <i
                    className="fa-solid fa-circle-check wod-card__feito"
                    style={{color: theme.primary}}
                    aria-label="Concluído"
                  />
                )}
              </header>

              {wod.description && <p className="wod-card__descricao">{wod.description}</p>}

              <footer className="wod-card__rodape" style={{color: theme.neutral}}>
                <button
                  type="button"
                  className="post-card__metrica post-card__acao"
                  style={{color: wod.isFavorited ? theme.primary : theme.text}}
                  disabled={ocupado !== null}
                  aria-pressed={Boolean(wod.isFavorited)}
                  onClick={() =>
                    agir(`${wod.id}:favorito`, () =>
                      favoritar({
                        variables: {
                          targetId: wod.id,
                          targetType: 'workout_group',
                          targetAction: acaoDeFavorito(wod),
                        },
                      }),
                    )
                  }
                >
                  <i className={`${wod.isFavorited ? 'fa-solid' : 'fa-light'} fa-star`} aria-hidden="true" />
                  {wod.isFavorited ? 'Favorito' : 'Favoritar'}
                </button>

                {/* Nao existe mutation para desmarcar conclusao: oferecer o
                    botao depois de concluido seria prometer o que a API nao faz. */}
                {!wod.completedOnDate &&
                  (confirmando === wod.id ? (
                    <>
                      <button
                        type="button"
                        className="post-card__metrica post-card__acao"
                        style={{color: theme.primary}}
                        disabled={ocupado !== null}
                        onClick={() =>
                          agir(`${wod.id}:conclusao`, () =>
                            concluir({
                              variables: {
                                targetId: wod.id,
                                targetType: 'workout_group',
                                completionDate: dataDeConclusao(wod, dia),
                              },
                            }),
                          )
                        }
                      >
                        <i className="fa-solid fa-circle-check" aria-hidden="true" />
                        {ocupado === `${wod.id}:conclusao` ? 'Marcando…' : 'Confirmar conclusão'}
                      </button>

                      <button
                        type="button"
                        className="post-card__metrica post-card__acao"
                        style={{color: theme.neutral}}
                        disabled={ocupado !== null}
                        onClick={() => setConfirmando(null)}
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="post-card__metrica post-card__acao"
                      style={{color: theme.text}}
                      disabled={ocupado !== null}
                      onClick={() => setConfirmando(wod.id)}
                    >
                      <i className="fa-light fa-circle-check" aria-hidden="true" />
                      Marcar como feito
                    </button>
                  ))}

                <Link to={wod.id} className="post-card__metrica post-card__acao" style={{color: theme.text}}>
                  <i className="fa-light fa-eye" aria-hidden="true" />
                  Ver treino
                </Link>

                {wod.video?.uri && (
                  <span className="post-card__metrica">
                    <i className="fa-light fa-circle-play" aria-hidden="true" />
                    vídeo
                  </span>
                )}
                <span className="post-card__metrica">
                  <i className="fa-light fa-comment" aria-hidden="true" />
                  {wod.comments?.total ?? 0}
                </span>
              </footer>
            </div>
          </article>
        ))}
    </div>
  );
};

// O dia fica aqui, e nao na lista: assim o aluno volta do detalhe para o mesmo
// dia que estava vendo, em vez de cair no de hoje.
export const WodsScreen: React.FC<Props> = ({theme, programFilter}) => {
  const [dia, setDia] = React.useState(() => new Date());

  return (
    <Routes>
      <Route index element={<WodsList theme={theme} programFilter={programFilter} dia={dia} setDia={setDia} />} />
      <Route path=":wodId" element={<WodDetailScreen theme={theme} />} />
    </Routes>
  );
};
